import { styled, useTheme } from '@mui/material';
import * as Highcharts from 'highcharts';
import HighchartsReact, { HighchartsReactRefObject } from 'highcharts-react-official';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Typography } from '../../atoms/Typography/Typography';
import { DoughnutChartDataType } from '../../../common/types';
import { useTranslation } from 'react-i18next';

export enum SizeVariant {
    // eslint-disable-next-line no-unused-vars
    small = 13,
    // eslint-disable-next-line no-unused-vars
    medium = 24
}

interface PortfolioPieChartInstance extends Highcharts.Chart {
    gapSeparators?: Highcharts.SVGElement[];
}

const SEPARATOR_WIDTH = 2;

const normalizeColor = (color?: Highcharts.ColorType) => {
    if (!color) {
        return undefined;
    }

    try {
        const colorObj = Highcharts?.color(color as Highcharts.ColorType);
        if (colorObj && typeof colorObj.get === 'function') {
            return (colorObj.get() as string).toLowerCase();
        }
    } catch (error) {
        if (color && typeof color === 'string') {
            return color?.trim().toLowerCase();
        }
    }

    return typeof color === 'string' ? color?.trim().toLowerCase() : undefined;
};

export const PortfolioIcon = ({
    color,
    variant = SizeVariant.medium,
    legendPosition
}: {
    color: string;
    variant?: SizeVariant;
    legendPosition?: LegendPosition;
}) => {
    return (
        <svg width={variant} height={variant} style={{ paddingTop: legendPosition === 'right' ? '4px' : 'inherit' }}>
            <rect
                width={legendPosition === 'right' ? '16' : '24'}
                height={legendPosition === 'right' ? '16' : '24'}
                rx="2"
                ry="2"
                fill={color}
            />
        </svg>
    );
};

export type LegendPosition = 'bottom' | 'right';

const StyledHighchartContainer = styled('div')<{ $legendPosition?: LegendPosition }>(({ $legendPosition }) => ({
    display: 'flex',
    flexDirection: $legendPosition === 'right' ? 'row' : 'column',
    alignItems: 'center'
}));

const StyledLegendContainer = styled('div')<{ $legendPosition?: LegendPosition }>(({ theme, $legendPosition }) => ({
    fontSize: theme.spacing(3),
    width: '200px',
    marginTop: $legendPosition === 'right' ? theme.spacing(0) : theme.spacing(4),
    marginLeft: $legendPosition === 'right' ? theme.spacing(3) : 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: $legendPosition === 'right' ? 'center' : 'flex-start'
}));

const StyledLegendItem = styled('div')<{ $legendPosition?: LegendPosition }>(({ theme, $legendPosition }) => ({
    display: 'flex',
    alignItems: 'center',
    verticalAlign: 'middle',
    gap: '12px',
    marginBottom: $legendPosition === 'right' ? theme.spacing(0) : theme.spacing(1.2),
    justifyContent: $legendPosition === 'right' ? 'flex-start' : 'space-between'
}));

const StyledLegendName = styled('div')<{ $legendPosition?: LegendPosition }>(({ theme, $legendPosition }) => ({
    display: 'flex',
    fontSize: $legendPosition === 'right' ? theme.spacing(1.5) : theme.spacing(2),
    alignItems: 'center',
    marginBottom: $legendPosition === 'right' ? theme.spacing(0) : theme.spacing(1.2)
}));

export const PortfolioPieChart = ({
    doughnutChartData,
    groupedPortfolio,
    isPDF,
    legendPosition = 'bottom',
    showSeparators = true,
    size,
    className
}: {
    doughnutChartData: DoughnutChartDataType[];
    groupedPortfolio: any;
    isPDF?: boolean;
    legendPosition?: LegendPosition;
    showSeparators?: boolean;
    size?: number;
    className?: string;
}) => {
    const theme = useTheme();
    const chartRef = useRef<HighchartsReactRefObject | null>(null);

    // Build the series data once from the incoming points. Used for BOTH the initial options and
    // updates so the very first paint already shows the slices (no empty donut -> data flash).
    const buildSeriesData = (points: DoughnutChartDataType[] = []) =>
        points.map((point) => ({
            ...point,
            // When separators are disabled, set each slice's border to its own color
            // to eliminate Highcharts anti-aliasing gaps between adjacent slices
            ...(!showSeparators && { borderColor: point?.color, borderWidth: 1 })
        }));

    const pieChartOptions: Highcharts.Options = {
        credits: {
            enabled: false
        },
        chart: {
            height: size ?? 240,
            width: size ?? 240,
            spacingTop: 20,
            margin: 0,
            type: 'pie',
            style: {
                fontFamily: 'TT Commons Pro'
            },
            animation: false
        },
        title: {
            text: ''
        },
        legend: {
            enabled: false
        },
        tooltip: {
            enabled: false,
            valueSuffix: '%',
            headerFormat: '<tspan style="font-size: 15px;">{point.key}</tspan>',
            style: {
                fontSize: '15px'
            }
        },
        plotOptions: {
            pie: {
                borderWidth: 0,
                borderColor: 'transparent',
                animation: false,
                allowPointSelect: false,
                states: {
                    hover: {
                        enabled: false
                    },
                    inactive: {
                        enabled: false
                    }
                }
            }
        },
        accessibility: {
            point: {
                valueSuffix: '%'
            },
            enabled: false // to remove accessibilty warning on console
        },
        series: [
            {
                dataLabels: {
                    enabled: false
                },
                name: 'Portfolio Composition',
                // @ts-ignore
                innerSize: '65.79%', // to match figma design - 107.87px of 164px
                type: 'pie',
                data: buildSeriesData(doughnutChartData),
                borderColor: showSeparators ? 'transparent' : undefined,
                borderWidth: showSeparators ? 0 : 1,
                animation: false
            }
        ]
    };

    const [chartOptions, setChartOptions] = useState(pieChartOptions);

    const updateHighcharts = useCallback(() => {
        const chartData = buildSeriesData(doughnutChartData);

        setChartOptions((prev) => {
            return {
                ...prev,
                series: [
                    {
                        dataLabels: {
                            enabled: false
                        },
                        name: 'Portfolio Composition',
                        innerSize: '65.79%', // to match figma design - 107.87px of 164px
                        // colorByPoint: true,
                        type: 'pie',
                        data: chartData,
                        borderColor: showSeparators ? 'transparent' : undefined,
                        borderWidth: showSeparators ? 0 : 1,
                        borderRadius: 0,
                        animation: false
                    }
                ]
            };
        });
    }, [doughnutChartData, isPDF, showSeparators]);

    useEffect(() => {
        updateHighcharts();
    }, [updateHighcharts, doughnutChartData]);

    useEffect(() => {
        if (!showSeparators) {
            return;
        }

        const chart = chartRef.current?.chart as PortfolioPieChartInstance | undefined;
        if (!chart) {
            return;
        }

        const renderSeparators = () => {
            const series = chart.series?.[0];
            const points = series?.points || [];

            chart.gapSeparators?.forEach((separator) => separator.destroy());
            chart.gapSeparators = [];

            if (!points.length) {
                return;
            }

            points.forEach((point, index) => {
                const shape = point.shapeArgs as
                    | (Highcharts.SVGAttributes & { start: number; r: number; innerR: number; x: number; y: number })
                    | undefined;
                if (!shape) {
                    return;
                }

                const previousPoint = points[(index - 1 + points.length) % points.length];
                if (!previousPoint) {
                    return;
                }

                const currentColor = normalizeColor(point.color as Highcharts.ColorType);
                const previousColor = normalizeColor(previousPoint.color as Highcharts.ColorType);

                if (!currentColor || !previousColor) {
                    return;
                }

                const isSameColor = currentColor === previousColor;

                const { start, r = 0, innerR = 0, x = 0, y = 0 } = shape;
                const cos = Math.cos(start);
                const sin = Math.sin(start);
                const outerX = x + cos * r;
                const outerY = y + sin * r;
                const innerX = x + cos * innerR;
                const innerY = y + sin * innerR;

                const separator = chart.renderer
                    .path([
                        ['M', innerX, innerY],
                        ['L', outerX, outerY]
                    ])
                    .attr({
                        stroke: isSameColor ? previousColor : theme.palette.neutral.white,
                        'stroke-width': isSameColor ? 1 : SEPARATOR_WIDTH,
                        zIndex: 5
                    } as Highcharts.SVGAttributes)
                    .add();

                if (!isSameColor) {
                    separator.element.setAttribute('stroke-linecap', 'round');
                }

                chart.gapSeparators!.push(separator);
            });
        };

        renderSeparators();
        Highcharts.addEvent(chart, 'render', renderSeparators);

        return () => {
            Highcharts.removeEvent(chart, 'render', renderSeparators);
            chart.gapSeparators?.forEach((separator) => separator.destroy());
            chart.gapSeparators = [];
        };
    }, [chartOptions, doughnutChartData, showSeparators]);
    const { t } = useTranslation();

    return (
        <StyledHighchartContainer $legendPosition={legendPosition} className={className}>
            <HighchartsReact
                highcharts={Highcharts}
                options={chartOptions}
                oneToOne={true}
                // [redraw, oneToOne, animation] passed to chart.update - animation=false so data
                // updates/redraws never animate the pie (prevents flicker).
                updateArgs={[true, true, false]}
                aria-label="Allocation interactive chart."
                ref={chartRef}
            />

            <StyledLegendContainer $legendPosition={legendPosition}>
                {groupedPortfolio?.map((_item: any, _index: number) => (
                    <StyledLegendItem key={_index} $legendPosition={legendPosition}>
                        <StyledLegendName $legendPosition={legendPosition}>
                            <PortfolioIcon color={_item.color} legendPosition={legendPosition} />
                            <Typography
                                variant={legendPosition === 'right' ? 'labels.small' : 'labels.large'}
                                label={
                                    legendPosition === 'right'
                                        ? `${t(_item.type.toUpperCase())}:`
                                        : t(_item.type.toUpperCase())
                                }
                                sx={{ marginLeft: 8, color: theme.palette.semantic.text.default }}
                            />
                        </StyledLegendName>
                        <Typography
                            label={_item.totalAllocatedvalue}
                            variant={legendPosition === 'right' ? 'labels.smallDemibold' : 'numbers.smallBold'}
                            sx={{ alignSelf: 'baseline', paddingTop: '4px', color: theme.palette.semantic.text.default }}
                        />
                    </StyledLegendItem>
                ))}
            </StyledLegendContainer>
        </StyledHighchartContainer>
    );
};
